import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Modal, Button, Form } from "react-bootstrap";
import {
  applyDefaultsToBoard,
  createColumn,
  createGroup,
  createItem,
  createSubItem,
  deleteColumn,
  deleteGroup,
  deleteItem,
  downloadCsv,
  getBoard,
  getBoardDefaults,
  listItems,
  setCellValue,
  updateColumn,
  updateGroup,
  updateItem,
} from "./api/boardApi";
import { CellRenderer } from "./components/cells";
import ItemPanel from "./components/ItemPanel";
import BoardToolbar from "./components/BoardToolbar";
import { Zap, Users, X, Check, ChevronRight, GripVertical, Clock } from "lucide-react";
import KanbanView from "./components/KanbanView";
import CalendarView from "./components/CalendarView";
import TimelineView from "./components/TimelineView";
import AutomationsModal from "./components/AutomationsModal";
import CsvImportModal from "./components/CsvImportModal";
import ShareDialog from "./components/ShareDialog";
import MembersDialog from "./components/MembersDialog";
import useBoardWs from "./useBoardWs";
import { getAllEmployees, errorToast, successToast } from "../../redux/actionCreator";
import { all_employees } from "../../redux/commonReducer";
import { getSession } from "../../utils/session";
import "./board.scss";

const COLUMN_TYPE_OPTIONS = [
  { type: "text", label: "Text" },
  { type: "long_text", label: "Long text" },
  { type: "number", label: "Number" },
  { type: "status", label: "Status" },
  { type: "people", label: "People" },
  { type: "date", label: "Date" },
  { type: "dropdown", label: "Dropdown" },
  { type: "checkbox", label: "Checkbox" },
  { type: "rating", label: "Rating" },
  { type: "link", label: "Link" },
  { type: "email", label: "Email" },
  { type: "phone", label: "Phone" },
  { type: "tags", label: "Tags" },
  { type: "file", label: "File" },
  { type: "timeline", label: "Timeline" },
  { type: "formula", label: "Formula" },
];

// Static fallback used while board-defaults is loading or on a fresh DB.
// Mirrors the factory defaults in the boards-service so the UI doesn't go
// blank if the network call fails.
const FALLBACK_KINDS = [
  { id: "task", label: "Task", color: "#c4c4c4", container: false },
  { id: "module", label: "Module", color: "#5559df", container: true },
  { id: "bug", label: "Bug", color: "#df2f4a", container: false },
  { id: "feature", label: "Feature", color: "#00c875", container: false },
];

export default function Board() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const employees = useSelector(all_employees) || [];

  const [board, setBoard] = useState(null);
  const [items, setItems] = useState([]);
  // Server-resolved creators / column-value authors keyed by _id. The
  // /v1/employees endpoint excludes admins/super_admins, so items created by
  // admins would resolve to "—" without this fallback. The list endpoint
  // returns a `people` array of every author it found in the page.
  const [extraPeople, setExtraPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});

  // ── View state (toolbar) ────────────────────────────────────────
  // Persist in URL hash so a refresh keeps the user's filter/sort/view.
  const initialView = useMemo(() => {
    try {
      const h = new URLSearchParams(window.location.hash.slice(1));
      const sysParam = h.get("sysCols");
      // Default: assigned_to + created_by + created_at on. Updated_on off
      // by default (less noisy out of the box). User can flip via toolbar.
      const defaultSys = ["assigned_to", "created_by", "created_at"];
      return {
        view: h.get("view") || "table",
        search: h.get("q") || "",
        sortCol: h.get("sortCol") || null,
        sortDir: h.get("sortDir") || "asc",
        groupBy: h.get("groupBy") || null,
        filters: h.get("filters") ? JSON.parse(decodeURIComponent(h.get("filters"))) : [],
        kanbanCol: h.get("kanbanCol") || null,
        sysCols: sysParam != null
          ? sysParam.split(",").filter(Boolean)
          : defaultSys,
      };
    } catch {
      return { view: "table", search: "", sortCol: null, sortDir: "asc", groupBy: null, filters: [], kanbanCol: null, sysCols: ["assigned_to", "created_by", "created_at"] };
    }
  }, []);

  const [view, setView] = useState(initialView.view);
  const [search, setSearch] = useState(initialView.search);
  const [sort, setSort] = useState({ col_id: initialView.sortCol, dir: initialView.sortDir });
  const [groupBy, setGroupBy] = useState(initialView.groupBy);
  const [filters, setFilters] = useState(initialView.filters);
  const [kanbanColId, setKanbanColId] = useState(initialView.kanbanCol);
  // Which system meta columns are visible in the table (Created by / on /
  // Updated on / Assigned to). Stored as a string[] for easy URL serialization.
  const [sysCols, setSysCols] = useState(initialView.sysCols);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [kinds, setKinds] = useState(FALLBACK_KINDS);
  const [kindIndex, setKindIndex] = useState(() => {
    const idx = {};
    for (const k of FALLBACK_KINDS) idx[k.id] = k;
    return idx;
  });
  const [addColForGroup, setAddColForGroup] = useState(null); // unused for now
  const [showAddCol, setShowAddCol] = useState(false);
  const [newCol, setNewCol] = useState({ type: "text", title: "", expression: "" });
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState("");

  const role = getSession()?.role || "employee";
  // Derive readOnly from the server-resolved role (see myBoardRole below).
  // Editors + owners can write; viewers and non-members can't. This is the
  // single source of truth — computing role client-side was unreliable for
  // legacy sessions whose localStorage didn't carry the user's `_id`.
  const readOnly = useMemo(() => {
    if (!board) return true;
    const r = board.my_role;
    if (r === "owner" || r === "editor") return false;
    if (r === "viewer") return true;
    // Backend didn't send my_role (older build, perhaps) — fall back to
    // the org-level role hint we already have.
    if (role === "admin" || role === "super_admin") return false;
    return true;
  }, [board, role]);

  // People eligible for assignment on this board. Strict project rule:
  // only existing board members (owner + members.role any) can be assigned.
  // Falls back to the full employees list ONLY when the board is still
  // loading, so a freshly-created board doesn't render an empty picker.
  const boardPeople = useMemo(() => {
    if (!board) return employees;
    const ids = new Set();
    if (board.owner) ids.add(String(board.owner));
    for (const m of board.members || []) ids.add(String(m.user));
    if (!ids.size) return employees;
    return employees.filter((e) => ids.has(String(e._id)));
  }, [board, employees]);

  // Effective board role for the current user. The backend's GET
  // /boards/:id resolves this from the JWT (taking into account org-level
  // admin, board ownership, members.role, and main-visibility fallback)
  // and ships it as `board.my_role`. Trust that single value — legacy
  // login sessions don't carry the user's _id, which was the most common
  // reason client-side resolution returned null → "I see read-only".
  const myBoardRole = useMemo(() => {
    if (!board) return null;
    if (board.my_role) return board.my_role;
    // Defensive fallback for older backend builds that don't ship my_role.
    if (role === "admin" || role === "super_admin") return "owner";
    const me = getSession()?._id;
    if (!me) return null;
    if (String(board.owner) === String(me)) return "owner";
    const m = (board.members || []).find((x) => String(x.user) === String(me));
    if (m) return m.role || "editor";
    if (!board.visibility || board.visibility === "main") return "editor";
    return null;
  }, [board, role]);

  const peopleIndex = useMemo(() => {
    const idx = {};
    for (const e of employees) idx[e._id] = e;
    // Merge server-resolved creators/authors. The boards-service list
    // response includes a `people` array covering every author present in
    // the current page — this catches items created by admins/super_admins
    // who are excluded from the /v1/employees endpoint.
    for (const p of extraPeople) {
      if (p?._id && !idx[p._id]) idx[p._id] = p;
    }
    // Finally, fold in the currently-logged-in user from session. Optimistic
    // updates (an item I just created) hit the table before /list refreshes;
    // this ensures my own name shows up immediately without waiting.
    const me = getSession();
    if (me?._id && !idx[me._id]) {
      const [firstname, ...rest] = (me.name || me.email || "").split(" ");
      idx[me._id] = {
        _id: me._id,
        email: me.email,
        firstname: firstname || me.email,
        lastname: rest.join(" "),
      };
    }
    return idx;
  }, [employees, extraPeople]);

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, iRes] = await Promise.all([getBoard(boardId), listItems(boardId, { limit: 500 })]);
      setBoard(bRes.data);
      setItems(iRes.data || []);
      setExtraPeople(iRes.people || []);
    } catch (e) {
      errorToast(e.message || "Failed to load board");
      if (e.payload?.message === "Board not found") navigate("/boards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!employees.length) dispatch(getAllEmployees({ page: 0, limit: 200 }));
    // Pull kinds from BoardDefaults so the +Add item dropdown reflects what
    // the super-admin has configured. Fallback to hardcoded list on failure
    // so the page never shows an empty menu.
    getBoardDefaults()
      .then((res) => {
        const ks = res.data?.kinds?.length ? res.data.kinds : FALLBACK_KINDS;
        setKinds(ks);
        const idx = {};
        for (const k of ks) idx[k.id] = k;
        setKindIndex(idx);
      })
      .catch(() => { /* keep fallback */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  // Persist toolbar state to the URL hash so reload/copy-link preserves it.
  useEffect(() => {
    const params = new URLSearchParams();
    if (view !== "table") params.set("view", view);
    if (search) params.set("q", search);
    if (sort.col_id) {
      params.set("sortCol", sort.col_id);
      params.set("sortDir", sort.dir);
    }
    if (groupBy) params.set("groupBy", groupBy);
    if (filters.length) params.set("filters", encodeURIComponent(JSON.stringify(filters)));
    if (kanbanColId) params.set("kanbanCol", kanbanColId);
    // Always serialize sysCols so an empty selection (user turned them all
    // off) also persists — distinct from "no key present" (use defaults).
    params.set("sysCols", sysCols.join(","));
    const h = params.toString();
    const newHash = h ? `#${h}` : "";
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
    }
  }, [view, search, sort, groupBy, filters, kanbanColId, sysCols]);

  // Filter + sort pipeline. Search & status filters run before sort. Kept
  // entirely client-side — fine for boards up to a few thousand items.
  const compareByColumn = (a, b, col, dir) => {
    const va = a.column_values?.[col._id]?.value;
    const vb = b.column_values?.[col._id]?.value;
    let cmp = 0;
    if (col.type === "number" || col.type === "rating") {
      cmp = (Number(va) || 0) - (Number(vb) || 0);
    } else if (col.type === "date") {
      const ta = va ? new Date(va).getTime() : 0;
      const tb = vb ? new Date(vb).getTime() : 0;
      cmp = ta - tb;
    } else if (col.type === "status") {
      // Sort by label order, with empties last.
      const labels = col.settings?.labels || [];
      const idxA = va ? labels.findIndex((l) => l.id === va) : Number.POSITIVE_INFINITY;
      const idxB = vb ? labels.findIndex((l) => l.id === vb) : Number.POSITIVE_INFINITY;
      cmp = idxA - idxB;
    } else if (col.type === "checkbox") {
      cmp = (va ? 1 : 0) - (vb ? 1 : 0);
    } else {
      const sa = String(va ?? "").toLowerCase();
      const sb = String(vb ?? "").toLowerCase();
      cmp = sa < sb ? -1 : sa > sb ? 1 : 0;
    }
    return dir === "desc" ? -cmp : cmp;
  };

  const visibleItems = useMemo(() => {
    let out = items;

    if (search) {
      const q = search.toLowerCase();
      out = out.filter((it) => (it.title || "").toLowerCase().includes(q));
    }

    for (const f of filters) {
      if (!f.label_ids || f.label_ids.length === 0) continue;
      out = out.filter((it) => {
        const v = it.column_values?.[f.col_id]?.value;
        return v && f.label_ids.includes(v);
      });
    }

    if (sort.col_id) {
      const col = (board?.columns || []).find((c) => c._id === sort.col_id);
      if (col) out = out.slice().sort((a, b) => compareByColumn(a, b, col, sort.dir));
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, filters, sort, board]);

  // ── Drag-to-reorder ─────────────────────────────────────────────
  // Items: drag a row onto another row to swap order, drop on a group
  // header to move to that group. Columns: drag a header onto another to
  // swap order. Both are HTML5 DnD with dataTransfer carrying the dragged
  // entity id, no extra deps.
  const [dragRowId, setDragRowId] = useState(null);
  const [dragRowOverId, setDragRowOverId] = useState(null);
  const [dragColId, setDragColId] = useState(null);
  const [dragColOverId, setDragColOverId] = useState(null);

  const handleRowDragStart = (e, item) => {
    if (readOnly) return;
    setDragRowId(item._id);
    e.dataTransfer.setData("text/x-board-item", item._id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleRowDragOver = (e, item) => {
    if (readOnly || !dragRowId || dragRowId === item._id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragRowOverId(item._id);
  };
  const handleRowDrop = async (e, target) => {
    e.preventDefault();
    setDragRowOverId(null);
    const dragId = e.dataTransfer.getData("text/x-board-item") || dragRowId;
    setDragRowId(null);
    if (!dragId || dragId === target._id) return;

    const draggedItem = items.find((i) => i._id === dragId);
    if (!draggedItem) return;
    if (String(draggedItem.parent_item_id || "") !== String(target.parent_item_id || "")) {
      // Don't allow dragging across parent boundaries — would need richer UX.
      return;
    }

    // Recompute order: place the dragged row immediately AFTER target.
    const siblings = items
      .filter((i) =>
        String(i.parent_item_id || "") === String(target.parent_item_id || "") &&
        String(i.group_id) === String(target.group_id),
      )
      .sort((a, b) => a.order - b.order);
    const filtered = siblings.filter((i) => i._id !== dragId);
    const tgtIdx = filtered.findIndex((i) => i._id === target._id);
    const reordered = [
      ...filtered.slice(0, tgtIdx + 1),
      draggedItem,
      ...filtered.slice(tgtIdx + 1),
    ];

    // Optimistic local update.
    const orderById = new Map(reordered.map((it, idx) => [it._id, idx]));
    setItems((arr) =>
      arr.map((it) => {
        if (!orderById.has(it._id)) return it;
        const nextGroup = target.group_id;
        return { ...it, group_id: nextGroup, order: orderById.get(it._id) };
      }),
    );

    // Persist only the moved row — the others' relative order is preserved
    // because their integers stay monotonic (with the dragged row inserted).
    try {
      await updateItem(boardId, draggedItem._id, {
        order: orderById.get(draggedItem._id),
        group_id: target.group_id,
      });
    } catch (err) {
      errorToast(err.message);
    }
  };

  const handleColDragStart = (e, col) => {
    if (readOnly) return;
    setDragColId(col._id);
    e.dataTransfer.setData("text/x-board-column", col._id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleColDragOver = (e, col) => {
    if (readOnly || !dragColId || dragColId === col._id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragColOverId(col._id);
  };
  const handleColDrop = async (e, target) => {
    e.preventDefault();
    setDragColOverId(null);
    const dragId = e.dataTransfer.getData("text/x-board-column") || dragColId;
    setDragColId(null);
    if (!dragId || dragId === target._id) return;

    const allCols = (board?.columns || []).slice().sort((a, b) => a.order - b.order);
    const filtered = allCols.filter((c) => c._id !== dragId);
    const tgtIdx = filtered.findIndex((c) => c._id === target._id);
    const dragged = allCols.find((c) => c._id === dragId);
    const reordered = [...filtered.slice(0, tgtIdx + 1), dragged, ...filtered.slice(tgtIdx + 1)];

    // Optimistic update.
    setBoard((b) => ({
      ...b,
      columns: reordered.map((c, i) => ({ ...c, order: i })),
    }));

    // Persist the dragged column's new order.
    try {
      await updateColumn(boardId, dragged._id, { order: reordered.findIndex((c) => c._id === dragged._id) });
    } catch (err) {
      errorToast(err.message);
    }
  };

  const handleSyncDefaults = async () => {
    try {
      const res = await applyDefaultsToBoard(boardId);
      successToast(res.message);
      if (res.data?.added > 0) {
        // Refetch the board to pick up the new columns.
        const b = await getBoard(boardId);
        setBoard(b.data);
      }
    } catch (e) {
      errorToast(e.message);
    }
  };

  // Live updates from other browsers. Events that originate from the current
  // client are filtered out server-side via exceptUserId, so we never
  // double-apply our own optimistic updates.
  useBoardWs(boardId, (event, payload) => {
    if (!event) return;
    switch (event) {
      case "item.created":
        if (payload?.item) {
          setItems((arr) => (arr.some((i) => i._id === payload.item._id) ? arr : [...arr, payload.item]));
        }
        break;
      case "item.updated":
        if (payload?.item_id && payload?.changes) {
          setItems((arr) =>
            arr.map((it) =>
              it._id === payload.item_id ? { ...it, ...stripUndefined(payload.changes) } : it,
            ),
          );
        }
        break;
      case "item.deleted":
        if (payload?.item_id) {
          const allRemoved = new Set([payload.item_id, ...(payload.cascaded_ids || [])]);
          setItems((arr) => arr.filter((it) => !allRemoved.has(it._id)));
          if (openItem && allRemoved.has(openItem._id)) setOpenItem(null);
        }
        break;
      case "cell.changed":
        if (payload?.item_id && payload?.column_id) {
          setItems((arr) =>
            arr.map((it) =>
              it._id === payload.item_id
                ? {
                    ...it,
                    column_values: {
                      ...(it.column_values || {}),
                      [payload.column_id]: {
                        value: payload.value,
                        type: payload.type,
                        updated_by: payload.actor,
                        updated_at: payload.createdAt,
                      },
                    },
                  }
                : it,
            ),
          );
        }
        break;
      case "column.created":
        if (payload?.column) {
          setBoard((b) =>
            b && !b.columns.some((c) => c._id === payload.column._id)
              ? { ...b, columns: [...b.columns, payload.column] }
              : b,
          );
        }
        break;
      case "column.updated":
        if (payload?.column) {
          setBoard((b) =>
            b
              ? { ...b, columns: b.columns.map((c) => (c._id === payload.column._id ? payload.column : c)) }
              : b,
          );
        }
        break;
      case "column.deleted":
        if (payload?.column_id) {
          setBoard((b) => (b ? { ...b, columns: b.columns.filter((c) => c._id !== payload.column_id) } : b));
        }
        break;
      case "group.created":
        if (payload?.group) {
          setBoard((b) =>
            b && !b.groups.some((g) => g._id === payload.group._id)
              ? { ...b, groups: [...b.groups, payload.group] }
              : b,
          );
        }
        break;
      case "group.updated":
        if (payload?.group) {
          setBoard((b) =>
            b ? { ...b, groups: b.groups.map((g) => (g._id === payload.group._id ? payload.group : g)) } : b,
          );
        }
        break;
      case "group.deleted":
        if (payload?.group_id) {
          setBoard((b) => (b ? { ...b, groups: b.groups.filter((g) => g._id !== payload.group_id) } : b));
          setItems((arr) => arr.filter((it) => it.group_id !== payload.group_id));
        }
        break;
      case "notification.added":
        // Re-emit globally so the topbar bell (which doesn't know about this
        // specific board's WS) can react in real time.
        if (typeof window !== "undefined" && payload?.user_id) {
          window.dispatchEvent(new CustomEvent("smarthatch:notification", { detail: payload }));
        }
        break;
      default:
        break;
    }
  });

  // Split items into top-level (no parent) per group + children index.
  // When a filter excludes a parent module BUT not its child, we still
  // surface the child by keeping its parent visible — Monday does the same.
  const { topByGroup, childrenByParent, virtualGroups } = useMemo(() => {
    // Always index children by parent from the full list so collapsed
    // module rows can still expand to show all their kids.
    const kids = {};
    for (const it of items) {
      if (!it.parent_item_id) continue;
      const pid = String(it.parent_item_id);
      if (!kids[pid]) kids[pid] = [];
      kids[pid].push(it);
    }
    for (const k of Object.keys(kids)) kids[k].sort((a, z) => a.order - z.order);

    // Determine which top-level items survive filtering. If a child matches
    // but its parent doesn't, surface the parent too (so the child can show).
    const visibleIds = new Set(visibleItems.map((i) => i._id));
    const surviving = items.filter((it) => {
      if (it.parent_item_id) return false; // only consider top-level here
      if (visibleIds.has(it._id)) return true;
      // Promote a hidden parent if any of its kids is visible.
      const childIds = (kids[String(it._id)] || []).map((c) => c._id);
      return childIds.some((cid) => visibleIds.has(cid));
    });

    // Two grouping modes: board-groups (default) or by a status column.
    let groups;
    if (groupBy) {
      const col = (board?.columns || []).find((c) => c._id === groupBy);
      const labels = col?.settings?.labels || [];
      groups = [
        ...labels.map((l) => ({ _id: `lbl:${l.id}`, title: l.label, color: l.color, order: 0, _matcher: (it) => it.column_values?.[col._id]?.value === l.id })),
        { _id: "lbl:__none__", title: "Uncategorized", color: "#dde2eb", order: 1, _matcher: (it) => !it.column_values?.[col._id]?.value },
      ];
    } else {
      groups = (board?.groups || []).slice().sort((a, z) => a.order - z.order).map((g) => ({
        ...g,
        _matcher: (it) => String(it.group_id) === String(g._id),
      }));
    }

    const top = {};
    for (const g of groups) {
      top[g._id] = surviving.filter(g._matcher).sort((a, z) => a.order - z.order);
    }
    return { topByGroup: top, childrenByParent: kids, virtualGroups: groups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, items, visibleItems, groupBy]);

  const sortedColumns = useMemo(
    () => [...(board?.columns || [])].sort((a, z) => a.order - z.order),
    [board],
  );

  // System (virtual) meta columns — computed from item fields, never stored
  // on the board. The user toggles them on/off via the toolbar Columns menu.
  // `position: "leading"` means it slots in BEFORE the user-defined columns
  // (right after the title col). Default for assigned_to — putting the owner
  // up front is what users expect when they're triaging. Everything else
  // appends to the right where it doesn't disrupt drag-reorder.
  const SYSTEM_COL_DEFS = useMemo(() => [
    { _id: "__sys_assigned_to", type: "__system", system: "assigned_to", title: "Assigned to", position: "leading" },
    { _id: "__sys_created_by", type: "__system", system: "created_by", title: "Created by", position: "trailing" },
    { _id: "__sys_created_at", type: "__system", system: "created_at", title: "Created on", position: "trailing" },
    { _id: "__sys_updated_at", type: "__system", system: "updated_at", title: "Updated on", position: "trailing" },
  ], []);

  const leadingSystemColumns = useMemo(
    () => SYSTEM_COL_DEFS.filter((c) => c.position === "leading" && sysCols.includes(c.system)),
    [SYSTEM_COL_DEFS, sysCols],
  );
  const trailingSystemColumns = useMemo(
    () => SYSTEM_COL_DEFS.filter((c) => c.position !== "leading" && sysCols.includes(c.system)),
    [SYSTEM_COL_DEFS, sysCols],
  );

  // Columns rendered left-to-right: title col (hardcoded) → leading system
  // cols (Assigned-to) → user-defined columns → trailing system cols.
  const displayColumns = useMemo(
    () => [...leadingSystemColumns, ...sortedColumns, ...trailingSystemColumns],
    [leadingSystemColumns, sortedColumns, trailingSystemColumns],
  );

  const titleForKind = (kindId) => {
    const k = kindIndex[kindId];
    return k ? `New ${k.label.toLowerCase()}` : "New item";
  };

  const handleAddItem = async (groupId, kind = "task") => {
    try {
      const res = await createItem(boardId, { title: titleForKind(kind), group_id: groupId, kind });
      setItems((arr) => [...arr, res.data]);
      if (kindIndex[kind]?.container) {
        setExpandedModules((m) => ({ ...m, [res.data._id]: true }));
      }
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleAddSubItem = async (parentItem, kind) => {
    try {
      const res = await createSubItem(boardId, parentItem._id, { title: titleForKind(kind), kind });
      setItems((arr) => [...arr, res.data]);
      setExpandedModules((m) => ({ ...m, [parentItem._id]: true }));
    } catch (e) {
      errorToast(e.message);
    }
  };

  // Non-container kinds make sense as children of a container (e.g. Bug
  // under Module). Containers nested inside containers usually don't.
  const childKinds = useMemo(() => kinds.filter((k) => !k.container), [kinds]);

  const toggleModule = (id) =>
    setExpandedModules((m) => ({ ...m, [id]: !m[id] }));

  const handleAddGroup = async () => {
    const t = newGroupTitle.trim();
    if (!t) return;
    try {
      const res = await createGroup(boardId, { title: t });
      setBoard((b) => ({ ...b, groups: [...b.groups, res.data] }));
      setShowAddGroup(false);
      setNewGroupTitle("");
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleAddColumn = async () => {
    const t = newCol.title.trim();
    if (!t) return;
    try {
      const body = { type: newCol.type, title: t };
      if (newCol.type === "formula" && newCol.expression?.trim()) {
        body.settings = { expression: newCol.expression.trim() };
      }
      const res = await createColumn(boardId, body);
      setBoard((b) => ({ ...b, columns: [...b.columns, res.data] }));
      setShowAddCol(false);
      setNewCol({ type: "text", title: "", expression: "" });
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleDeleteColumn = async (colId) => {
    if (!window.confirm("Delete this column and all its values?")) return;
    try {
      await deleteColumn(boardId, colId);
      setBoard((b) => ({ ...b, columns: b.columns.filter((c) => c._id !== colId) }));
      successToast("Column deleted");
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Delete this group and all its items?")) return;
    try {
      await deleteGroup(boardId, groupId);
      setBoard((b) => ({ ...b, groups: b.groups.filter((g) => g._id !== groupId) }));
      setItems((arr) => arr.filter((it) => it.group_id !== groupId));
      successToast("Group deleted");
    } catch (e) {
      errorToast(e.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await deleteItem(boardId, itemId);
      setItems((arr) => arr.filter((it) => it._id !== itemId));
      if (openItem?._id === itemId) setOpenItem(null);
    } catch (e) {
      errorToast(e.message);
    }
  };

  const commitCellInline = async (item, colId, value) => {
    try {
      const res = await setCellValue(boardId, item._id, colId, value);
      setItems((arr) =>
        arr.map((it) =>
          it._id === item._id
            ? {
                ...it,
                column_values: {
                  ...(it.column_values || {}),
                  [colId]: {
                    value: res.data.value,
                    type: res.data.type,
                    updated_by: res.data.updated_by,
                    updated_at: res.data.updated_at,
                  },
                },
              }
            : it,
        ),
      );
    } catch (e) {
      errorToast(e.message);
    }
  };

  const commitTitleInline = async (item, title) => {
    const next = title.trim();
    if (!next || next === item.title) return;
    try {
      const res = await updateItem(boardId, item._id, { title: next });
      setItems((arr) => arr.map((it) => (it._id === item._id ? { ...it, title: res.data.title } : it)));
    } catch (e) {
      errorToast(e.message);
    }
  };

  // Ensures the board has a People column we can write assignees into.
  // Used by the Assigned-to system cell — older boards predate the
  // BoardDefaults entry, so we create one on demand. Returns the column
  // object (existing or freshly created).
  const ensureAssigneeColumn = async () => {
    const existing = (board?.columns || []).find((c) => c.type === "people");
    if (existing) return existing;
    try {
      const res = await createColumn(boardId, {
        type: "people",
        title: "Assigned to",
        settings: { multiple: true },
      });
      const col = res.data;
      setBoard((b) => (b ? { ...b, columns: [...(b.columns || []), col] } : b));
      successToast("Added \"Assigned to\" column");
      return col;
    } catch (e) {
      errorToast(e.message || "Failed to add People column");
      return null;
    }
  };

  const commitGroupTitleInline = async (group, title) => {
    const next = title.trim();
    if (!next || next === group.title) return;
    try {
      const res = await updateGroup(boardId, group._id, { title: next });
      setBoard((b) =>
        b
          ? {
              ...b,
              groups: (b.groups || []).map((g) =>
                g._id === group._id ? { ...g, title: res.data?.title || next } : g,
              ),
            }
          : b,
      );
    } catch (e) {
      errorToast(e.message);
    }
  };

  if (loading) return <div className="board-loading">Loading…</div>;
  if (!board) return null;

  return (
    <div className="board-page">
      <header className="board-head">
        <button className="back-btn" onClick={() => navigate("/boards")}>‹ Boards</button>
        <h2>{board.name}</h2>
        <div className="board-head-actions">
          {!readOnly && (
            <Button variant="outline-secondary" size="sm" onClick={() => setShowAutomations(true)} title="Manage automations">
              <Zap size={14} className="me-1" /> Automate
            </Button>
          )}
          <Dropdown align="end">
            <Dropdown.Toggle as="button" className="board-head-iconbtn" title="More">⋯</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => downloadCsv(boardId, `${board.name}.csv`).catch((e) => errorToast(e.message))}>
                Export to CSV
              </Dropdown.Item>
              {!readOnly && (
                <Dropdown.Item onClick={() => setShowCsvImport(true)}>Import from CSV</Dropdown.Item>
              )}
              {!readOnly && (
                <Dropdown.Item onClick={handleSyncDefaults}>Sync default columns</Dropdown.Item>
              )}
              {!readOnly && <Dropdown.Divider />}
              {!readOnly && (
                <Dropdown.Item onClick={() => setShowShare(true)}>Share &amp; manage links…</Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowMembers(true)}
            title="View or manage board members"
          >
            <Users size={14} className="me-1" /> Members
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowAddGroup(true)} disabled={readOnly}>
            + Group
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowAddCol(true)} disabled={readOnly}>
            + Column
          </Button>
        </div>
      </header>

      <BoardToolbar
        board={board}
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        view={view}
        onViewChange={setView}
        sysCols={sysCols}
        onSysColsChange={setSysCols}
        systemColDefs={SYSTEM_COL_DEFS}
      />

      {view === "kanban" && (
        <KanbanBranch
          board={board}
          items={visibleItems.filter((i) => !i.parent_item_id)}
          peopleIndex={peopleIndex}
          readOnly={readOnly}
          kindIndex={kindIndex}
          kanbanColId={kanbanColId}
          setKanbanColId={setKanbanColId}
          onCardClick={setOpenItem}
          onCellCommit={commitCellInline}
        />
      )}

      {view === "calendar" && (
        <CalendarView
          board={board}
          items={visibleItems.filter((i) => !i.parent_item_id)}
          peopleIndex={peopleIndex}
          kindIndex={kindIndex}
          onCardClick={setOpenItem}
        />
      )}

      {view === "timeline" && (
        <TimelineView
          board={board}
          items={visibleItems.filter((i) => !i.parent_item_id)}
          kindIndex={kindIndex}
          readOnly={readOnly}
          onCardClick={setOpenItem}
          onCellCommit={(item, colId, value) => commitCellInline(item, colId, value)}
        />
      )}

      {view === "table" && (
      <div className="board-table-wrap">
        {virtualGroups.map((g) => {
            const topItems = topByGroup[g._id] || [];
            const totalCount =
              topItems.length +
              topItems.reduce((n, it) => n + (childrenByParent[it._id]?.length || 0), 0);
            return (
              <section key={g._id} className="group-block">
                <header className="group-head" style={{ borderLeftColor: g.color }}>
                  <EditableGroupTitle
                    group={g}
                    readOnly={readOnly}
                    onCommit={(t) => commitGroupTitleInline(g, t)}
                  />
                  <span className="group-count">{totalCount} items</span>
                  {!readOnly && (
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="link" size="sm" className="group-menu-btn">⋯</Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleDeleteGroup(g._id)}>Delete group</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </header>
                <table className="board-table">
                  <thead>
                    <tr>
                      <th className="col-title">Item</th>
                      {displayColumns.map((c) => {
                        const isSystem = !!c.system;
                        return (
                          <th
                            key={c._id}
                            className={`col-head ${isSystem ? "system" : ""} ${dragColOverId === c._id ? "drag-col-over" : ""} ${dragColId === c._id ? "dragging" : ""}`}
                            // System columns are virtual (no _id on the
                            // server) so drag-reorder + delete don't apply.
                            draggable={!readOnly && !isSystem}
                            onDragStart={isSystem ? undefined : (e) => handleColDragStart(e, c)}
                            onDragOver={isSystem ? undefined : (e) => handleColDragOver(e, c)}
                            onDragLeave={isSystem ? undefined : () => setDragColOverId(null)}
                            onDrop={isSystem ? undefined : (e) => handleColDrop(e, c)}
                            onDragEnd={isSystem ? undefined : () => { setDragColId(null); setDragColOverId(null); }}
                          >
                            <span className="col-head-inner">
                              {!isSystem && <span className="col-grip" title="Drag to reorder"><GripVertical size={12} /></span>}
                              <span className="col-head-title">
                                {c.title}
                                {isSystem && <span className="sys-tag" title="System column"><Clock size={10} /></span>}
                              </span>
                            </span>
                            {!readOnly && !isSystem && (
                              <Dropdown align="end">
                                <Dropdown.Toggle variant="link" size="sm" className="col-menu-btn">⋯</Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleDeleteColumn(c._id)}>Delete column</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                            {!readOnly && isSystem && (
                              <button
                                type="button"
                                className="col-hide-btn"
                                title="Hide this system column"
                                aria-label="Hide column"
                                onClick={() => setSysCols((prev) => prev.filter((s) => s !== c.system))}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </th>
                        );
                      })}
                      <th className="col-end" />
                    </tr>
                  </thead>
                  <tbody>
                    {topItems.map((it) => {
                      const isContainer = !!kindIndex[it.kind]?.container;
                      const children = childrenByParent[it._id] || [];
                      const expanded = !!expandedModules[it._id];
                      return (
                        <React.Fragment key={it._id}>
                          <ItemRow
                            item={it}
                            columns={displayColumns}
                            readOnly={readOnly}
                            peopleIndex={peopleIndex}
                            allPeople={boardPeople}
                            kindIndex={kindIndex}
                            isContainer={isContainer}
                            childCount={isContainer ? children.length : 0}
                            expanded={expanded}
                            onToggleExpand={isContainer ? () => toggleModule(it._id) : null}
                            onOpen={() => setOpenItem(it)}
                            onTitleCommit={(t) => commitTitleInline(it, t)}
                            onCellCommit={(colId, v) => commitCellInline(it, colId, v)}
                            onEnsureAssigneeColumn={ensureAssigneeColumn}
                            onDelete={() => handleDeleteItem(it._id)}
                            dragState={{
                              isDragging: dragRowId === it._id,
                              isOver: dragRowOverId === it._id,
                              onDragStart: (e) => handleRowDragStart(e, it),
                              onDragOver: (e) => handleRowDragOver(e, it),
                              onDragLeave: () => setDragRowOverId(null),
                              onDrop: (e) => handleRowDrop(e, it),
                              onDragEnd: () => { setDragRowId(null); setDragRowOverId(null); },
                            }}
                          />
                          {isContainer && expanded && children.map((child) => (
                            <ItemRow
                              key={child._id}
                              item={child}
                              columns={displayColumns}
                              readOnly={readOnly}
                              peopleIndex={peopleIndex}
                              allPeople={boardPeople}
                              kindIndex={kindIndex}
                              isChild
                              onOpen={() => setOpenItem(child)}
                              onTitleCommit={(t) => commitTitleInline(child, t)}
                              onCellCommit={(colId, v) => commitCellInline(child, colId, v)}
                              onEnsureAssigneeColumn={ensureAssigneeColumn}
                              onDelete={() => handleDeleteItem(child._id)}
                              dragState={{
                                isDragging: dragRowId === child._id,
                                isOver: dragRowOverId === child._id,
                                onDragStart: (e) => handleRowDragStart(e, child),
                                onDragOver: (e) => handleRowDragOver(e, child),
                                onDragLeave: () => setDragRowOverId(null),
                                onDrop: (e) => handleRowDrop(e, child),
                                onDragEnd: () => { setDragRowId(null); setDragRowOverId(null); },
                              }}
                            />
                          ))}
                          {isContainer && expanded && !readOnly && (
                            <tr className="add-subitem-row">
                              <td colSpan={sortedColumns.length + 2}>
                                {childKinds.map((k) => (
                                  <button
                                    key={k.id}
                                    className="add-item-btn"
                                    onClick={() => handleAddSubItem(it, k.id)}
                                  >
                                    + Sub {k.label.toLowerCase()}
                                  </button>
                                ))}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {!readOnly && (
                      <tr className="add-item-row">
                        <td colSpan={sortedColumns.length + 2}>
                          <Dropdown>
                            <Dropdown.Toggle as="button" className="add-item-btn add-item-dropdown">
                              + Add item
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {kinds.map((k) => (
                                <Dropdown.Item key={k.id} onClick={() => handleAddItem(g._id, k.id)}>
                                  <span
                                    className="kind-swatch"
                                    style={{ background: k.color }}
                                  />
                                  {k.label}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>
            );
          })}
      </div>
      )}

      {showAutomations && (
        <AutomationsModal board={board} onClose={() => setShowAutomations(false)} />
      )}

      {showCsvImport && (
        <CsvImportModal
          board={board}
          onClose={() => setShowCsvImport(false)}
          onImported={async () => {
            try {
              const items = await listItems(boardId, { limit: 500 });
              setItems(items.data || []);
            } catch (e) { errorToast(e.message); }
          }}
        />
      )}

      {showMembers && (
        <MembersDialog
          show={showMembers}
          onHide={() => setShowMembers(false)}
          board={board}
          myRole={myBoardRole}
          allEmployees={employees}
          peopleIndex={peopleIndex}
          onBoardChange={(next) => setBoard((b) => (next ? { ...b, ...next } : b))}
        />
      )}
      {showShare && (
        <ShareDialog board={board} onClose={() => setShowShare(false)} />
      )}

      {openItem && (
        <ItemPanel
          board={board}
          item={items.find((i) => i._id === openItem._id) || openItem}
          peopleIndex={peopleIndex}
          allPeople={boardPeople}
          readOnly={readOnly}
          onClose={() => setOpenItem(null)}
          onItemChange={(next) =>
            setItems((arr) => arr.map((it) => (it._id === next._id ? { ...it, ...next } : it)))
          }
        />
      )}

      <Modal show={showAddCol} onHide={() => setShowAddCol(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add column</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={newCol.type}
              onChange={(e) => setNewCol((c) => ({ ...c, type: e.target.value }))}
            >
              {COLUMN_TYPE_OPTIONS.map((o) => (
                <option key={o.type} value={o.type}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={newCol.title}
              autoFocus
              onChange={(e) => setNewCol((c) => ({ ...c, title: e.target.value }))}
              placeholder="e.g. Status"
            />
          </Form.Group>
          {newCol.type === "formula" && (
            <Form.Group>
              <Form.Label>Expression</Form.Label>
              <Form.Control
                value={newCol.expression}
                onChange={(e) => setNewCol((c) => ({ ...c, expression: e.target.value }))}
                placeholder={'{Estimated SP} * 2 + ROUND({Rating}, 1)'}
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
              <Form.Text className="text-muted">
                Reference other columns with <code>{"{Column Title}"}</code>. Supports
                + − × ÷ % comparisons, plus SUM, AVG, MIN, MAX, ROUND, IF, AND, OR, NOT.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddCol(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddColumn} disabled={!newCol.title.trim()}>Add</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddGroup} onHide={() => setShowAddGroup(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add group</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={newGroupTitle}
              autoFocus
              onChange={(e) => setNewGroupTitle(e.target.value)}
              placeholder="e.g. To do"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddGroup(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddGroup} disabled={!newGroupTitle.trim()}>Add</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// Small subtitle showing who created the item and (if a People column has a
// value) the first assignee. Resolves names via the employees index. Keeps
// the row visually compact — names truncate with ellipsis.
function ItemPeople({ item, columns, peopleIndex }) {
  const creator = peopleIndex[item.created_by];
  const creatorName = creator
    ? `${creator.firstname || ""} ${creator.lastname || ""}`.trim() || creator.email
    : null;

  // First People column with a value on this item → render up to 2 assignees.
  const peopleCol = (columns || []).find((c) => c.type === "people");
  const raw = peopleCol ? item.column_values?.[peopleCol._id]?.value : null;
  const assigneeIds = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const assignees = assigneeIds
    .map((id) => {
      const p = peopleIndex[id];
      return p ? `${p.firstname || ""} ${p.lastname || ""}`.trim() || p.email : null;
    })
    .filter(Boolean);

  if (!creatorName && assignees.length === 0) return null;

  return (
    <div className="title-meta">
      {creatorName && (
        <span className="meta-item" title={`Created by ${creatorName}`}>
          <span className="meta-label">by</span> {creatorName}
        </span>
      )}
      {assignees.length > 0 && (
        <span className="meta-item meta-assignee" title={`Assigned to ${assignees.join(", ")}`}>
          <span className="meta-label">·</span>{" "}
          <span className="meta-arrow">→</span> {assignees.slice(0, 2).join(", ")}
          {assignees.length > 2 && ` +${assignees.length - 2}`}
        </span>
      )}
    </div>
  );
}

// Renderer for the four virtual "system" columns. Three are read-only
// (created_by, created_at, updated_at). The fourth — assigned_to — shadows
// the first People column on the board and IS editable via an inline picker.
// Committing the picker routes through the same setCellValue path real
// cells use, so the underlying People column stays the source of truth.
function SystemCell({
  system,
  item,
  columns,
  peopleIndex,
  allPeople = [],
  readOnly,
  onAssignedCommit,
  onEnsureAssigneeColumn,
}) {
  if (system === "created_by") {
    const u = peopleIndex?.[item.created_by];
    if (!u) return <span className="sys-empty">—</span>;
    const name = `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email;
    return (
      <span className="sys-person" title={u.email || ""}>
        <span className="avatar-chip sys-avatar">
          {(name || "?").slice(0, 1).toUpperCase()}
        </span>
        <span className="sys-person-name">{name}</span>
      </span>
    );
  }
  if (system === "created_at") return <SysDate ts={item.createdAt || item.created_at} />;
  if (system === "updated_at") return <SysDate ts={item.updated_at || item.updatedAt} />;
  if (system === "assigned_to") {
    return (
      <AssignedToCell
        item={item}
        columns={columns}
        peopleIndex={peopleIndex}
        allPeople={allPeople}
        readOnly={readOnly}
        onCommit={onAssignedCommit}
        onEnsureColumn={onEnsureAssigneeColumn}
      />
    );
  }
  return <span className="sys-empty">—</span>;
}

// Editable Assigned-to picker.
//
// Two graceful paths:
//   1. The board already has a People column → reads/writes its value
//      directly via setCellValue (onCommit).
//   2. No People column on the board → first click triggers onEnsureColumn
//      to create one ("Assigned to", multiple), then auto-opens the picker
//      so the user can finish what they started in a single gesture.
//
// The picker portals to <body> with fixed-position coords from the trigger's
// bounding rect — same approach as StatusCell — so it can't get clipped by
// any ancestor's overflow or stacking context.
function AssignedToCell({ item, columns, peopleIndex, allPeople, readOnly, onCommit, onEnsureColumn }) {
  const peopleCol = (columns || []).find((c) => c.type === "people");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [wantOpen, setWantOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // After onEnsureColumn resolves, the new column propagates via setBoard
  // and re-renders this cell with `peopleCol` populated. If the user
  // expressed "wantOpen", flip the picker on once that happens.
  useEffect(() => {
    if (wantOpen && peopleCol) {
      setWantOpen(false);
      setOpen(true);
    }
  }, [wantOpen, peopleCol]);

  // Position the portal picker against the trigger.
  useLayoutEffectAlias(() => {
    if (!open) return;
    const update = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight || 260;
      const menuW = menuRef.current?.offsetWidth || 240;
      const vpH = window.innerHeight;
      const vpW = window.innerWidth;
      const spaceBelow = vpH - r.bottom;
      const placement = spaceBelow >= menuH + 8 || spaceBelow >= r.top ? "below" : "above";
      const top = placement === "below"
        ? Math.min(r.bottom + 4, vpH - menuH - 8)
        : Math.max(8, r.top - menuH - 4);
      const left = Math.min(Math.max(8, r.left), vpW - menuW - 8);
      setPos({ top, left });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const multiple = peopleCol ? !!peopleCol.settings?.multiple : true;
  const raw = peopleCol ? item.column_values?.[peopleCol._id]?.value : null;
  const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];

  const handleOpen = async () => {
    if (readOnly) return;
    if (!peopleCol) {
      // Lazily create the column then signal "auto-open once it appears".
      if (!onEnsureColumn || pending) return;
      setPending(true);
      const col = await onEnsureColumn();
      setPending(false);
      if (col) setWantOpen(true);
      return;
    }
    setOpen((o) => !o);
  };

  const toggle = (uid) => {
    if (!onCommit || !peopleCol) return;
    if (multiple) {
      const next = selected.includes(uid)
        ? selected.filter((x) => x !== uid)
        : [...selected, uid];
      onCommit(peopleCol._id, next.length ? next : null);
    } else {
      setOpen(false);
      onCommit(peopleCol._id, uid === selected[0] ? null : [uid]);
    }
  };

  const names = selected
    .map((id) => {
      const p = peopleIndex?.[id];
      return p
        ? `${p.firstname || ""} ${p.lastname || ""}`.trim() || p.email
        : null;
    })
    .filter(Boolean);

  return (
    <div className="cell-display assigned-to-wrap">
      <button
        ref={btnRef}
        type="button"
        className="sys-person assigned-to-btn"
        disabled={readOnly || pending}
        onClick={handleOpen}
        title={
          !peopleCol
            ? "Click to add an Assigned-to column and assign"
            : names.length
            ? names.join(", ")
            : "Click to assign"
        }
      >
        {pending ? (
          <span className="sys-empty">Adding column…</span>
        ) : selected.length === 0 ? (
          <span className="sys-empty">— Assign</span>
        ) : (
          <>
            {names.slice(0, 2).map((n, i) => (
              <span key={i} className="avatar-chip sys-avatar">
                {(n || "?").slice(0, 1).toUpperCase()}
              </span>
            ))}
            <span className="sys-person-name">
              {names.slice(0, 2).join(", ")}
              {names.length > 2 && ` +${names.length - 2}`}
            </span>
          </>
        )}
      </button>
      {open &&
        peopleCol &&
        createPortal(
          <>
            <div className="cell-menu-backdrop" onClick={() => setOpen(false)} />
            <div
              ref={menuRef}
              className="cell-menu people-menu people-menu-portal"
              style={{ top: pos.top, left: pos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {allPeople.length === 0 && (
                <div className="people-empty">No employees loaded</div>
              )}
              {allPeople.map((p) => {
                const isSel = selected.includes(p._id);
                return (
                  <button
                    key={p._id}
                    type="button"
                    className={`people-menu-item ${isSel ? "selected" : ""}`}
                    onClick={() => toggle(p._id)}
                  >
                    <span className="avatar-chip">
                      {((p.firstname?.[0] || "") + (p.lastname?.[0] || "")).toUpperCase() || "?"}
                    </span>
                    <span className="people-menu-name">
                      {`${p.firstname || ""} ${p.lastname || ""}`.trim() || p.email}
                    </span>
                    {isSel && <span className="people-check"><Check size={12} /></span>}
                  </button>
                );
              })}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

// `useLayoutEffect` runs before paint so the picker's first frame already
// has the right coords. Alias matches the SSR-safe pattern used elsewhere.
const useLayoutEffectAlias = React.useLayoutEffect;

// Compact absolute timestamp with relative-time tooltip. Avoids extra deps
// (no date-fns) — the format is fine for board-table density.
function SysDate({ ts }) {
  if (!ts) return <span className="sys-empty">—</span>;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return <span className="sys-empty">—</span>;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const short = sameYear
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
  return (
    <span className="sys-date" title={d.toLocaleString()}>
      {short}
    </span>
  );
}

// Click-to-edit group title in the table view. Stays a static span until
// the user clicks; then becomes an input that commits on Enter or blur,
// cancels on Escape. Read-only viewers (share-link visitors, members) see
// the span only.
function EditableGroupTitle({ group, readOnly, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.title);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      // Sync draft from the current value, then focus + select all so the
      // user can either type a replacement or click to position the caret.
      setDraft(group.title);
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [editing, group.title]);

  if (readOnly) {
    return <span className="group-title">{group.title}</span>;
  }
  if (!editing) {
    return (
      <span
        className="group-title editable"
        title="Click to rename"
        onClick={() => setEditing(true)}
      >
        {group.title}
      </span>
    );
  }
  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== group.title) onCommit(draft.trim());
  };
  return (
    <input
      ref={inputRef}
      type="text"
      className="group-title-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setEditing(false);
        }
      }}
    />
  );
}

function stripUndefined(o) {
  const out = {};
  for (const k of Object.keys(o || {})) if (o[k] !== undefined) out[k] = o[k];
  return out;
}

// Picks the status column kanban groups by. Defaults to system_key="status"
// if present, then the first status column, then nothing (shows a notice).
function KanbanBranch({
  board,
  items,
  peopleIndex,
  readOnly,
  kindIndex,
  kanbanColId,
  setKanbanColId,
  onCardClick,
  onCellCommit,
}) {
  const statusCols = (board.columns || []).filter((c) => c.type === "status");
  const chosenCol =
    (kanbanColId && statusCols.find((c) => c._id === kanbanColId)) ||
    statusCols.find((c) => c.system_key === "status") ||
    statusCols[0] ||
    null;

  if (!chosenCol) {
    return (
      <div className="kanban-empty-board">
        Kanban needs at least one <strong>status column</strong> on the board. Add one (e.g. "Status")
        and pick it as the grouping column.
      </div>
    );
  }

  return (
    <>
      <div className="kanban-controls">
        <span className="kanban-label">Group cards by:</span>
        <select
          className="kanban-select"
          value={chosenCol._id}
          onChange={(e) => setKanbanColId(e.target.value)}
        >
          {statusCols.map((c) => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
      </div>
      <KanbanView
        items={items}
        groupByColumn={chosenCol}
        board={board}
        readOnly={readOnly}
        peopleIndex={peopleIndex}
        kindIndex={kindIndex}
        onCardClick={onCardClick}
        onCellCommit={onCellCommit}
      />
    </>
  );
}

function ItemRow({
  item,
  columns,
  readOnly,
  peopleIndex,
  allPeople,
  onOpen,
  onTitleCommit,
  onCellCommit,
  onEnsureAssigneeColumn,
  onDelete,
  isContainer,
  isChild,
  childCount,
  expanded,
  onToggleExpand,
  kindIndex = {},
  dragState,
}) {
  const [titleDraft, setTitleDraft] = useState(item.title);
  const [editingTitle, setEditingTitle] = useState(false);

  useEffect(() => {
    setTitleDraft(item.title);
  }, [item.title]);

  const kind = item.kind || "task";
  const kindDef = kindIndex[kind];
  const showBadge = kindDef && kind !== "task";
  const rowCls = [
    "item-row",
    `kind-${kind}`,
    isContainer ? "is-module" : "",
    isChild ? "is-child" : "",
    dragState?.isDragging ? "is-dragging" : "",
    dragState?.isOver ? "drag-over" : "",
  ].filter(Boolean).join(" ");

  return (
    <tr
      className={rowCls}
      draggable={!readOnly && !!dragState}
      onDragStart={dragState?.onDragStart}
      onDragOver={dragState?.onDragOver}
      onDragLeave={dragState?.onDragLeave}
      onDrop={dragState?.onDrop}
      onDragEnd={dragState?.onDragEnd}
    >
      <td className="cell title-cell">
        {editingTitle && !readOnly ? (
          <input
            className="cell-input"
            value={titleDraft}
            autoFocus
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={() => {
              setEditingTitle(false);
              onTitleCommit(titleDraft);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
              if (e.key === "Escape") {
                setTitleDraft(item.title);
                setEditingTitle(false);
              }
            }}
          />
        ) : (
          <div className="title-row">
            {!readOnly && dragState && (
              <span className="row-grip" title="Drag to reorder"><GripVertical size={12} /></span>
            )}
            {isChild && <span className="child-indent" />}
            {isContainer && (
              <button
                className={`expand-caret ${expanded ? "open" : ""}`}
                onClick={onToggleExpand}
                title={expanded ? "Collapse" : "Expand"}
                aria-label={expanded ? "Collapse" : "Expand"}
              >
                <ChevronRight size={12} />
              </button>
            )}
            {showBadge && (
              <span
                className="kind-badge"
                style={{ background: kindDef.color }}
              >
                {isChild ? `Sub ${kindDef.label.toLowerCase()}` : kindDef.label}
              </span>
            )}
            <button className="open-btn" title="Open item" onClick={onOpen}>↗</button>
            <div className="title-stack">
              <span
                className="title-text"
                onClick={() => !readOnly && setEditingTitle(true)}
              >
                {item.title}
              </span>
              <ItemPeople item={item} columns={columns} peopleIndex={peopleIndex} />
            </div>
            {isContainer && childCount > 0 && (
              <span className="child-count" title={`${childCount} sub-items`}>{childCount}</span>
            )}
            {!readOnly && (
              <button className="row-del" onClick={onDelete} title="Delete" aria-label="Delete row"><X size={12} /></button>
            )}
          </div>
        )}
      </td>
      {columns.map((c) => {
        if (c.system) {
          return (
            <td key={c._id} className={`cell type-system system-${c.system}`}>
              <SystemCell
                system={c.system}
                item={item}
                columns={columns}
                peopleIndex={peopleIndex}
                allPeople={allPeople}
                readOnly={readOnly}
                // Assigned-to is the only writable system cell — it shadows
                // the first People column on the board. onCommit routes
                // through the same setCellValue path real cells use.
                onAssignedCommit={(peopleColId, value) =>
                  onCellCommit(peopleColId, value)
                }
                onEnsureAssigneeColumn={onEnsureAssigneeColumn}
              />
            </td>
          );
        }
        return (
          <td key={c._id} className={`cell type-${c.type}`}>
            <CellRenderer
              column={c}
              value={item.column_values?.[c._id]?.value ?? null}
              readOnly={readOnly}
              peopleIndex={peopleIndex}
              allPeople={allPeople}
              item={item}
              boardId={item.board_id}
              onCommit={(v) => onCellCommit(c._id, v)}
            />
          </td>
        );
      })}
      <td className="cell col-end" />
    </tr>
  );
}
